import React, { createContext, useContext, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { selectPatientDetails } from "../store/selector";
import { CONSENT_STATUS_TYPES } from "../constant/consent";
import ACTIONS from "../store/action";
import { getLatestConsentEventAPI } from "../api/api";
import { mapEventTypeToStatus } from "../utils/eConsentHelper";

/**
 * Context for consent status state management
 */
const ConsentStatusContext = createContext();

export const ConsentStatusProvider = ({ children }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const patientDetails = useSelector(selectPatientDetails);
  const patientId = patientDetails?.id;
  const latestConsentEvent = patientDetails?.latestConsentEvent;

  const [loading, setLoading] = useState(false);

  const mapConsentEventToStatus = useCallback(
    (consentEvent) => {
      if (!consentEvent) {
        return { status: CONSENT_STATUS_TYPES.NO_RECORD };
      }

      return {
        status: mapEventTypeToStatus(consentEvent.type),
        type: consentEvent.type,
        createdAt: consentEvent.createdAt ? new Date(consentEvent.createdAt) : null,
        effectiveDate: consentEvent.effectiveDate ? new Date(consentEvent.effectiveDate) : null,
        userName: consentEvent.userName,
        patientName: patientDetails?.name,
        doneBy: consentEvent.doneBy,
        via: consentEvent.via
      };
    },
    [patientDetails, t]
  );

  const consentStatus = mapConsentEventToStatus(latestConsentEvent);

  const refreshConsentStatus = useCallback(async () => {
    if (!patientId) return;

    setLoading(true);
    try {
      const { data: latestEvent } = await getLatestConsentEventAPI(patientId);
      dispatch(ACTIONS.updatePatientConsentEvent(latestEvent));
    } catch (err) {
      console.error("Failed to refresh consent status:", err);
    } finally {
      setLoading(false);
    }
  }, [patientId, dispatch]);

  return (
    <ConsentStatusContext.Provider
      value={{
        consentStatus,
        refreshConsentStatus,
        loading
      }}
    >
      {children}
    </ConsentStatusContext.Provider>
  );
};

export const useConsentStatusContext = () => {
  const context = useContext(ConsentStatusContext);
  if (!context) {
    throw new Error("useConsentStatusContext must be used within a ConsentStatusProvider");
  }
  return context;
};
